import { useEffect, useReducer } from "react";
import { subscribePageTourEnded } from "../tour/tourEvents";

// ---------------------------------------------------------------------------
// Module-level open-state store for the Ledger drawer — mirrors paletteStore so
// the HELP nav entry and the palette's Tab-escalation can summon the drawer
// without prop-drilling. Carries the summon opts (prefill/chip/profileHint) so
// the drawer knows whether to auto-send a first message or show its chips.
// (03 §3.3 entry points.)
// ---------------------------------------------------------------------------
export const ledgerStore = { open: false, opts: null, listeners: new Set() };

/**
 * Open the Ledger drawer.
 * @param {Object} [opts]
 * @param {string} [opts.prefill]     text auto-sent as the drawer's first message
 * @param {string} [opts.chip]        canned-chip launch ("problem" → "Something's wrong")
 * @param {string} [opts.profileHint] classifier profile hint ("nav"|"howto"|"problem")
 * @param {boolean} [opts.postTour]   reopened after a Ledger-launched tour ended —
 *                                    the episode persisted; show the follow-up chip
 */
export function openLedger(opts = {}) {
  ledgerStore.open = true;
  ledgerStore.opts = opts;
  ledgerStore.listeners.forEach((l) => l());
}

export function closeLedger() {
  if (!ledgerStore.open) return;
  ledgerStore.open = false;
  ledgerStore.opts = null;
  ledgerStore.listeners.forEach((l) => l());
}

/** Subscribe to drawer open state + the current summon opts. */
export function useLedgerOpen() {
  const [, force] = useReducer((n) => n + 1, 0);
  useEffect(() => {
    ledgerStore.listeners.add(force);
    return () => ledgerStore.listeners.delete(force);
  }, []);
  return { open: ledgerStore.open, opts: ledgerStore.opts };
}

// Auto-reopen after a Ledger-launched tour ends (03 §3.3 revised). The episode
// survived the drawer close that the tour launch triggered, so bring the drawer
// back with a post-tour follow-up affordance. "?"-button tours (launchedFrom
// "hud"/"palette"/"help") never trigger this — only "ledger".
subscribePageTourEnded(({ launchedFrom } = {}) => {
  if (launchedFrom === "ledger") openLedger({ postTour: true });
});
